import React, { useState, useCallback } from "react";
import { FileUpload } from "./FileUpload";
import { ImageGenerator } from "./ImageGenerator";
import { VideoGenerator } from "./VideoGenerator";
import { ImageEditor } from "./ImageEditor";
import { fileToBase64 } from "../services/geminiService";

export const MainStudio = ({ onLogout }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [imageToEdit, setImageToEdit] = useState(null);
  const [activeTab, setActiveTab] = useState("photo");
  const [videoSource, setVideoSource] = useState(null);
  console.log("check videosource image",videoSource);
  const handleFilesUpload = useCallback(async (filesWithViews) => {
    try {
      const filesWithBase64 = await Promise.all(
        filesWithViews.map(async (fileData) => {
          const base64 = await fileToBase64(fileData.file);
          return {
            file: fileData.file,
            base64,
            mimeType: fileData.file.type,
            viewType: fileData.viewType,
            customLabel: fileData.customLabel,
          };
        })
      );

      setUploadedFiles(filesWithBase64);
      setVideoSource(filesWithBase64[0]);
      setGeneratedImages([]);
      setImageToEdit(null);
      setActiveTab("photo");
    } catch (error) {
      console.error("Error converting files to base64:", error);
    }
  }, []);

  const handleReset = () => {
    setUploadedFiles([]);
    setGeneratedImages([]);
    setImageToEdit(null);
    setVideoSource(null);
  };

  const handleEditImage = (image) => {
    setImageToEdit(image);
    setActiveTab("edit");
  };

  const handleCreateVideo = (image) => {
    setVideoSource({ base64: image.base64, mimeType: image.mimeType });
    setActiveTab("video");
  };

  const handleUpdateImage = (updatedImage) => {
    setGeneratedImages((prev) =>
      prev.map((img) => (img.id === updatedImage.id ? updatedImage : img))
    );
    setImageToEdit(updatedImage);
  };

  const handleBackToGallery = () => {
    setImageToEdit(null);
    setActiveTab("photo");
  };

  const getViewIcon = (type) => {
    const icons = {
      front: "🔄",
      back: "🔄",
      left: "⬅️",
      right: "➡️",
      closeup: "🔍",
      lifestyle: "👕",
      detail: "📸",
      other: "📎",
    };
    return icons[type] || "📎";
  };

  const getDisplayLabel = (f) => {
    if (f.customLabel) return f.customLabel;
    const labels = {
      front: "Front View",
      back: "Back View",
      left: "Left Side",
      right: "Right Side",
      closeup: "Close Up",
      lifestyle: "Lifestyle",
      detail: "Detail",
      other: "Other View",
    };
    return labels[f.viewType] || "Unknown View";
  };

  const renderContent = () => {
    if (!uploadedFiles.length) {
      return <FileUpload onFilesUpload={handleFilesUpload} />;
    }

    if (activeTab === "edit" && imageToEdit) {
      return (
        <ImageEditor
          image={imageToEdit}
          onImageUpdate={handleUpdateImage}
          onBack={handleBackToGallery}
        />
      );
    }

    const sourceForDisplay =
      activeTab === "video" ? videoSource : uploadedFiles[0];

    return (
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 ">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* LEFT PANEL */}
          <div className="lg:w-1/3">
            <div className="bg-[#270b79] bg-opacity-50 rounded-2xl p-6 shadow-2xl border border-[#F06B32] border-opacity-30">
              <h2 className="text-xl font-bold text-white mb-4">
                {activeTab === "video" ? "Video Source" : "Reference Images"}
              </h2>

              {uploadedFiles.length > 0 && (
                <div className="space-y-4 mb-4 max-h-[600px] overflow-y-auto pr-2">
                  {uploadedFiles.map((fileObj, index) => (
                    <div
                      key={index}
                      className="bg-[#1a074f] rounded-xl p-4 shadow-lg border border-[#270b79]"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <span className="bg-[#F06B32] text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                          <span>{getViewIcon(fileObj.viewType)}</span>
                          <span>{getDisplayLabel(fileObj)}</span>
                        </span>

                        {index === 0 && activeTab === "video" && (
                          <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                            Video Source
                          </span>
                        )}

                        {index === 0 && activeTab === "photo" && (
                          <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                            Primary
                          </span>
                        )}
                      </div>

                      <img
                        src={fileObj.base64}
                        alt=""
                        className="rounded-lg w-full object-contain max-h-48 shadow-md mx-auto"
                      />
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={handleReset}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 ease-in-out transform hover:scale-105 shadow-lg mb-4"
              >
                📁 Upload New Images
              </button>

              {uploadedFiles.length > 1 && (
                <div className="p-3 bg-blue-900 bg-opacity-30 rounded-lg border border-blue-500 border-opacity-30">
                  <p className="text-sm text-blue-200 text-center">
                    <strong>🎯 Multiple References Active</strong>
                    <br />
                    Using {uploadedFiles.length} reference images for better AI
                    accuracy.
                  </p>
                </div>
              )}

              {/* TAB BUTTONS */}
              <div className="mt-4 space-y-2">
                <button
                  onClick={() => setActiveTab("photo")}
                  className={`w-full text-left p-3 rounded-lg ${
                    activeTab === "photo"
                      ? "bg-[#F06B32] bg-opacity-20 border border-[#F06B32]"
                      : "bg-[#270b79] bg-opacity-30 hover:bg-opacity-50"
                  }`}
                >
                  🎨 AI Photo Generation
                </button>

                <button
                  onClick={() => {
                    setActiveTab("video");
                    setVideoSource(uploadedFiles[0]);
                  }}
                  className={`w-full text-left p-3 rounded-lg ${
                    activeTab === "video"
                      ? "bg-[#F06B32] bg-opacity-20 border border-[#F06B32]"
                      : "bg-[#270b79] bg-opacity-30 hover:bg-opacity-50"
                  }`}
                >
                  🎬 AI Video Generation
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="lg:w-2/3">
            {activeTab === "photo" && (
              <ImageGenerator
                uploadedFiles={uploadedFiles}
                generatedImages={generatedImages}
                setGeneratedImages={setGeneratedImages}
                onEditImage={handleEditImage}
                onCreateVideo={handleCreateVideo}
              />
            )}

            {activeTab === "video" && videoSource && (
              <VideoGenerator uploadedFile={videoSource} />
            )}
          </div>
        </div>
      </div>
    );
  };

  return renderContent();
};
